import React, { useCallback, useEffect, useState } from "react";
import Sidebar from "../sidebar/sidebar";
import Header from "../header/header";
import { usePermission } from "../../hooks/usePermission";
import AiUsagePanel from "../AiUsage/AiUsagePanel";
import AgentProgressModal from "./AgentProgressModal";
import AgentStatusPanel from "./AgentStatusPanel";
import AnalyzeCompanyCard from "./AnalyzeCompanyCard";
import CompetitorSelector from "./CompetitorSelector";
import ContactAdminModal from "./ContactAdminModal";
import EsgBenchmarkTable from "./EsgBenchmarkTable";
import KpiDetailModal from "./KpiDetailModal";
import OverallPositionCard from "./OverallPositionCard";
import PeerLandscapeTable from "./PeerLandscapeTable";
import PeriodCompareControls from "./PeriodCompareControls";
import WhyModal from "./WhyModal";
import { sectionPayload, togglePeer } from "./helpers";
import { useCompetitorAnalysisData } from "./useCompetitorAnalysisData";
import { useRunAnalysis } from "./useRunAnalysis";
import "./AICompetitorAnalysis.css";

/**
 * AI Competitor Analysis.
 *
 * @param {object} props - Router props, used for sidebar highlighting.
 * @returns {JSX.Element} The page.
 *
 * @remarks
 * Reachable by every role. Running an analysis needs the
 * competitor-analysis.compare capability, and an attempt without it opens the
 * contact-admin dialog rather than hiding the controls — a disabled button with
 * no explanation reads as a broken page. The API enforces the same rule, so the
 * dialog is guidance rather than the security boundary.
 */
const AICompetitorAnalysis = (props) => {
  const { hasPermission, capabilitiesLoaded } = usePermission();
  const canRun = hasPermission("competitor-analysis.compare");

  const { context, latestRun, loading, error, reload } =
    useCompetitorAnalysisData();
  const {
    run,
    recommending,
    starting,
    error: runError,
    forbidden,
    dismissForbidden,
    startRecommendation,
    startAnalysis,
  } = useRunAnalysis(latestRun);

  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [sectorName, setSectorName] = useState("");
  const [baseCompanyName, setBaseCompanyName] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [mode, setMode] = useState("ai");
  const [blocked, setBlocked] = useState(false);
  const [period, setPeriod] = useState("latest");
  const [compareMode, setCompareMode] = useState("peer");
  const [selectedKpi, setSelectedKpi] = useState(null);
  const [why, setWhy] = useState(null);

  useEffect(() => {
    if (!context) return;
    setSectorName(context.sectorName || "");
    setBaseCompanyName(context.baseCompanyName || "");
  }, [context]);

  useEffect(() => {
    if (run?.competitors?.length) {
      setSelectedIds(
        run.competitors.filter((peer) => peer.isSelected).map((peer) => peer.id),
      );
    }
  }, [run]);

  /**
   * Runs an action only when the signed-in user may, otherwise explains why not.
   *
   * @param {function} action - What to run when permitted.
   * @returns {void}
   */
  const guard = useCallback(
    (action) => {
      if (!capabilitiesLoaded) return;
      if (!canRun) {
        setBlocked(true);
        return;
      }
      action();
    },
    [canRun, capabilitiesLoaded],
  );

  const industryId = context?.industryId ?? null;
  const showSelector = run?.status === "AWAITING_SELECTION";

  const landscape = sectionPayload(run, "PEER_LANDSCAPE");
  const benchmark = sectionPayload(run, "ESG_BENCHMARK");
  const position = sectionPayload(run, "OVERALL_POSITION");
  const hasResults = Boolean(landscape || benchmark || position);
  const years = benchmark?.years || position?.years || [];

  return (
    <div className="aica-shell">
      <div
        style={{ flex: sidebarExpanded ? "0 0 21%" : "0 0 60px" }}
        className="aica-sidebar"
      >
        <Sidebar
          dataFromParent={props.location.pathname}
          onSidebarToggle={setSidebarExpanded}
        />
      </div>

      <div className="aica-main">
        <Header />

        <div className="aica-content">
          <div className="aica-page-head">
            <div>
              <div className="aica-crumb">AI Competitor Analysis</div>
              <h1>AI Competitor Analysis</h1>
              <p className="aica-tagline">
                Understand how your ESG performance compares with your industry
                peers — powered by AI.
              </p>
            </div>
            <span className="aica-chip">Sourced from public disclosures</span>
          </div>

          {loading && <div className="aica-card aica-muted">Loading…</div>}

          {!loading && error && (
            <div className="aica-card aica-error">
              <p>{error}</p>
              <button
                type="button"
                className="aica-btn aica-btn-secondary"
                onClick={reload}
              >
                Try again
              </button>
            </div>
          )}

          {!loading && !error && context && (
            <>
              <AiUsagePanel
                title="AI usage & credits"
                highlightFeature="COMPETITOR_ANALYSIS"
              />

              <AnalyzeCompanyCard
                context={context}
                sectorName={sectorName}
                baseCompanyName={baseCompanyName}
                onSectorChange={setSectorName}
                onCompanyChange={setBaseCompanyName}
                busy={recommending}
                onGetCompetitors={() =>
                  guard(() =>
                    startRecommendation({
                      sectorName,
                      baseCompanyName,
                      industryId,
                    }),
                  )
                }
              />

              {runError && <div className="aica-card aica-error">{runError}</div>}

              {showSelector && (
                <CompetitorSelector
                  competitors={run.competitors}
                  selectedIds={selectedIds}
                  mode={mode}
                  onModeChange={setMode}
                  onToggle={(id) =>
                    setSelectedIds((current) => togglePeer(current, id))
                  }
                  onWhy={() =>
                    setWhy({
                      title: "Why these competitors?",
                      body: run.peerRationale,
                    })
                  }
                  busy={starting}
                  onRunAnalysis={(ids) => guard(() => startAnalysis(ids))}
                />
              )}

              <AgentStatusPanel run={run} />

              {hasResults && (
                <>
                  <PeriodCompareControls
                    period={period}
                    compareMode={compareMode}
                    availableYears={years.length || 1}
                    onPeriodChange={setPeriod}
                    onCompareChange={setCompareMode}
                  />

                  <OverallPositionCard
                    payload={position}
                    period={period}
                    compareMode={compareMode}
                  />

                  <EsgBenchmarkTable
                    payload={benchmark}
                    period={period}
                    compareMode={compareMode}
                    onSelectKpi={setSelectedKpi}
                    onWhyKpis={() =>
                      setWhy({
                        title: "Why these KPIs?",
                        body: benchmark?.whyTheseKpis,
                      })
                    }
                    onWhyMateriality={() =>
                      setWhy({
                        title: "Why does this matter?",
                        body: benchmark?.materiality,
                      })
                    }
                  />

                  <PeerLandscapeTable payload={landscape} />
                </>
              )}
            </>
          )}
        </div>
      </div>

      <AgentProgressModal run={run} />

      <KpiDetailModal
        kpi={selectedKpi}
        years={years}
        compareMode={compareMode}
        onClose={() => setSelectedKpi(null)}
      />

      <WhyModal
        show={Boolean(why)}
        title={why?.title || ""}
        body={why?.body}
        onClose={() => setWhy(null)}
      />

      <ContactAdminModal
        show={blocked || forbidden}
        onClose={() => {
          setBlocked(false);
          dismissForbidden();
        }}
      />
    </div>
  );
};

export default AICompetitorAnalysis;
